// React 17 shim for shadcn's Command primitive.
// The canonical implementation wraps `cmdk`, but every cmdk version (incl. 0.1.0)
// uses React 18 hooks (`useId`, `useSyncExternalStore`). On React 18 migration,
// replace this file with the standard cmdk-backed shadcn wrapper — same exports,
// no consumer changes.
import * as React from 'react'

import { SearchIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

interface ICommandContext {
  search: string
  setSearch: (value: string) => void
  itemCount: number
  registerItemMatch: (id: string, value: string, matches: boolean) => void
  unregisterItem: (id: string) => void
  highlightOnPress: boolean
  onSelectItem: (id: string) => void
  selectedId: string | null
  setSelectedId: (id: string | null) => void
  registerSelectHandler: (id: string, handler: () => void) => void
  unregisterSelectHandler: (id: string) => void
  shouldScrollOnNextSelectRef: React.MutableRefObject<boolean>
}

const CommandContext = React.createContext<ICommandContext | null>(null)

function useCommandContext(): ICommandContext {
  const ctx = React.useContext(CommandContext)
  if (!ctx) throw new Error('Command primitives must be used inside <Command>')
  return ctx
}

let nextId = 0
const useId = (): string => {
  const ref = React.useRef<string>()
  if (!ref.current) ref.current = `cmd-${++nextId}`
  return ref.current
}

// exact-after-trim, like cmdk: a looser match would work here and silently stop
// working once this file is cmdk-backed
const findIdByValue = (
  ids: string[],
  values: Map<string, string>,
  value?: string,
): string | null => {
  const target = value?.trim()
  if (!target) return null
  return ids.find(id => values.get(id)?.trim() === target) ?? null
}

interface CommandProps extends React.ComponentProps<'div'> {
  shouldFilter?: boolean
  /** Park the cursor on row 1 so Enter has a target. Off for touch. */
  autoHighlightFirst?: boolean
  /** Highlight while pressed — touch has no hover, so a tap has no feedback. */
  highlightOnPress?: boolean
  /** Value of the item the opening cursor lands on, read once at open. */
  defaultValue?: string
}

function Command({
  className,
  children,
  shouldFilter = true,
  autoHighlightFirst = true,
  highlightOnPress = false,
  defaultValue,
  ...props
}: CommandProps) {
  const [search, setSearch] = React.useState('')
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const matchesRef = React.useRef<Map<string, boolean>>(new Map())
  const valuesRef = React.useRef<Map<string, string>>(new Map())
  const handlersRef = React.useRef<Map<string, () => void>>(new Map())
  const orderRef = React.useRef<string[]>([])
  const [matchVersion, setMatchVersion] = React.useState(0)

  const registerItemMatch = React.useCallback(
    (id: string, value: string, matches: boolean) => {
      // before the early return: a re-registration can carry only a new value
      valuesRef.current.set(id, value)
      const prev = matchesRef.current.get(id)
      if (prev === matches) return
      matchesRef.current.set(id, matches)
      if (!orderRef.current.includes(id)) orderRef.current.push(id)
      setMatchVersion(v => v + 1)
    },
    [],
  )

  const unregisterItem = React.useCallback((id: string) => {
    matchesRef.current.delete(id)
    valuesRef.current.delete(id)
    handlersRef.current.delete(id)
    orderRef.current = orderRef.current.filter(x => x !== id)
    setMatchVersion(v => v + 1)
  }, [])

  const registerSelectHandler = React.useCallback(
    (id: string, handler: () => void) => {
      handlersRef.current.set(id, handler)
    },
    [],
  )

  const unregisterSelectHandler = React.useCallback((id: string) => {
    handlersRef.current.delete(id)
  }, [])

  const onSelectItem = React.useCallback((id: string) => {
    handlersRef.current.get(id)?.()
  }, [])

  const visibleIds = React.useMemo(() => {
    return orderRef.current.filter(id => matchesRef.current.get(id))
  }, [matchVersion])

  const itemCount = visibleIds.length

  const shouldScrollOnNextSelectRef = React.useRef(false)
  const hasLandedRef = React.useRef(false)
  const prevMatchVersionRef = React.useRef(matchVersion)

  React.useEffect(() => {
    if (!visibleIds.length) {
      if (selectedId !== null) setSelectedId(null)
      return
    }
    // re-rank the cursor onto the new top match — but only when WE filtered:
    // an externally driven set can change under a cursor the user placed
    const didResultsChange = prevMatchVersionRef.current !== matchVersion
    prevMatchVersionRef.current = matchVersion

    const landing = hasLandedRef.current
      ? null
      : findIdByValue(visibleIds, valuesRef.current, defaultValue)
    hasLandedRef.current = true

    const next =
      landing ??
      ((!didResultsChange || !shouldFilter) && visibleIds.includes(selectedId)
        ? selectedId
        : autoHighlightFirst
          ? (visibleIds[0] ?? null)
          : null)

    if (next === selectedId) return
    if (next) shouldScrollOnNextSelectRef.current = true
    setSelectedId(next)
  }, [
    visibleIds,
    selectedId,
    matchVersion,
    autoHighlightFirst,
    defaultValue,
    shouldFilter,
  ])

  const move = React.useCallback(
    (delta: number) => {
      if (!visibleIds.length) return
      const idx = selectedId ? visibleIds.indexOf(selectedId) : -1
      shouldScrollOnNextSelectRef.current = true
      if (idx === -1) {
        setSelectedId(
          delta > 0 ? visibleIds[0] : visibleIds[visibleIds.length - 1],
        )
        return
      }
      setSelectedId(
        visibleIds[(idx + delta + visibleIds.length) % visibleIds.length],
      )
    },
    [visibleIds, selectedId],
  )

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      move(1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      move(-1)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (selectedId) onSelectItem(selectedId)
    }
  }

  const value = React.useMemo<ICommandContext>(
    () => ({
      search: shouldFilter ? search : '',
      setSearch,
      itemCount,
      registerItemMatch,
      unregisterItem,
      highlightOnPress,
      onSelectItem,
      selectedId,
      setSelectedId,
      registerSelectHandler,
      unregisterSelectHandler,
      shouldScrollOnNextSelectRef,
    }),
    [
      search,
      shouldFilter,
      itemCount,
      registerItemMatch,
      unregisterItem,
      highlightOnPress,
      onSelectItem,
      selectedId,
      registerSelectHandler,
      unregisterSelectHandler,
    ],
  )

  return (
    <CommandContext.Provider value={value}>
      <div
        data-slot="command"
        className={cn(
          'tw:flex tw:h-full tw:w-full tw:flex-col tw:overflow-hidden tw:rounded-md tw:bg-popover tw:text-popover-foreground',
          className,
        )}
        onKeyDown={onKeyDown}
        {...props}
      >
        {children}
      </div>
    </CommandContext.Provider>
  )
}

interface CommandInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> {
  value?: string
  onValueChange?: (value: string) => void
}

function CommandInput({
  className,
  value,
  onValueChange,
  ...props
}: CommandInputProps) {
  const { search, setSearch } = useCommandContext()
  const isControlled = value !== undefined
  const current = isControlled ? value : search

  return (
    <div
      data-slot="command-input-wrapper"
      className="tw:flex tw:h-9 tw:items-center tw:gap-2 tw:border-b tw:border-border tw:px-3"
    >
      <SearchIcon className="tw:size-4 tw:shrink-0 tw:opacity-50" />
      <input
        data-slot="command-input"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        className={cn(
          'tw:flex tw:h-10 tw:w-full tw:border-0 tw:bg-transparent tw:py-3 tw:text-app-dense tw:outline-none tw:ring-0 tw:placeholder:text-muted-foreground tw:focus:outline-none tw:focus:ring-0 tw:focus-visible:outline-none tw:focus-visible:ring-0 tw:disabled:cursor-not-allowed tw:disabled:opacity-50',
          className,
        )}
        value={current}
        onChange={e => {
          const v = e.target.value
          if (!isControlled) setSearch(v)
          onValueChange?.(v)
        }}
        {...props}
      />
    </div>
  )
}

function CommandList({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="command-list"
      role="listbox"
      className={cn(
        'tw:max-h-[300px] tw:scroll-py-1 tw:overflow-x-hidden tw:overflow-y-auto',
        className,
      )}
      {...props}
    />
  )
}

function CommandEmpty({ className, ...props }: React.ComponentProps<'div'>) {
  const { itemCount, search } = useCommandContext()
  if (itemCount > 0 || !search) return null
  return (
    <div
      data-slot="command-empty"
      className={cn('tw:py-6 tw:text-center tw:text-app-dense', className)}
      {...props}
    />
  )
}

function CommandGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="command-group"
      role="group"
      className={cn('tw:overflow-hidden tw:p-1 tw:text-foreground', className)}
      {...props}
    />
  )
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="command-separator"
      role="separator"
      className={cn('tw:-mx-1 tw:h-px tw:bg-border', className)}
      {...props}
    />
  )
}

interface CommandItemProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onSelect'
> {
  /** Identity; must be unique. Also the haystack when no `keywords` are given. */
  value: string
  /** Search terms, so `value` can stay a stable id. */
  keywords?: string[]
  onSelect?: () => void
  disabled?: boolean
}

function CommandItem({
  className,
  value,
  keywords,
  onSelect,
  disabled,
  children,
  ...props
}: CommandItemProps) {
  const id = useId()
  const ctx = useCommandContext()
  const ctxRef = React.useRef(ctx)
  ctxRef.current = ctx
  const ref = React.useRef<HTMLDivElement>(null)

  // joined: a literal array prop is a new identity every render
  const haystack = keywords?.length ? keywords.join(' ') : value

  const matches = React.useMemo(() => {
    if (!ctx.search) return true
    return haystack.toLowerCase().includes(ctx.search.toLowerCase())
  }, [ctx.search, haystack])

  React.useEffect(() => {
    ctxRef.current.registerItemMatch(id, value, matches && !disabled)
  }, [id, value, matches, disabled])

  React.useEffect(() => {
    return () => ctxRef.current.unregisterItem(id)
  }, [id])

  React.useEffect(() => {
    if (!onSelect) return
    ctxRef.current.registerSelectHandler(id, onSelect)
    return () => ctxRef.current.unregisterSelectHandler(id)
  }, [id, onSelect])

  const isSelected = ctx.selectedId === id

  React.useEffect(() => {
    if (isSelected && ref.current && ctx.shouldScrollOnNextSelectRef.current) {
      ref.current.scrollIntoView({ block: 'nearest' })
      ctx.shouldScrollOnNextSelectRef.current = false
    }
  }, [isSelected, ctx.shouldScrollOnNextSelectRef])

  if (!matches) return null

  return (
    <div
      ref={ref}
      data-slot="command-item"
      role="option"
      aria-selected={isSelected || undefined}
      data-selected={isSelected || undefined}
      data-disabled={disabled || undefined}
      onMouseEnter={disabled ? undefined : () => ctx.setSelectedId(id)}
      // Touch has no hover, and `:active` alone is momentary and unreliable on
      // iOS for a non-button element. Highlighting on pointerdown gives a tap
      // the same feedback a mouse gets from hover.
      onPointerDown={
        disabled || !ctx.highlightOnPress
          ? undefined
          : () => ctx.setSelectedId(id)
      }
      // a press that becomes a scroll must not stay highlighted
      onPointerCancel={
        disabled || !ctx.highlightOnPress
          ? undefined
          : () => ctx.setSelectedId(null)
      }
      onPointerLeave={
        disabled || !ctx.highlightOnPress
          ? undefined
          : () => ctx.setSelectedId(null)
      }
      onClick={disabled ? undefined : () => onSelect?.()}
      className={cn(
        'tw:relative tw:flex tw:cursor-default tw:items-center tw:gap-2 tw:rounded-sm tw:px-2 tw:py-1.5 tw:text-app-dense tw:outline-hidden tw:select-none tw:data-[selected]:bg-accent tw:data-[selected]:text-accent-foreground tw:data-[disabled]:pointer-events-none tw:data-[disabled]:opacity-50 tw:[&_svg]:pointer-events-none tw:[&_svg]:shrink-0 tw:[&_svg:not([class*=size-])]:size-4',
        ctx.highlightOnPress &&
          'tw:active:bg-accent tw:active:text-accent-foreground',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
}
